import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError, AppThunkAPIType, Problem } from "./app/appTypes";
import { showForbidden } from "./features/notifications/notificationsSlice";
import { invalidateToken, selectBearerToken } from "./features/user/userSlice";

/** Body of a 403 from the backend's PackageAccess filter: the package and the level it needed. */
interface PackageForbidden {
    error: string
    package: string
    access: string
}

/**
 * Turns a failed response into the thunk's rejection value. 401 invalidates the token. A 403 that
 * isn't a problem+json (the company-role filter, a system-role check) shows a "no permission" toast
 * and stays on the page; problem+json 403s are left to the caller's own error handling.
 */
async function httpError(response: Response, dispatch: (action: any) => unknown): Promise<APIError> {
    if (response.status === 401) {
        dispatch(invalidateToken())
    }

    const mimeType = response.headers.get('content-type')
    if (mimeType?.startsWith('application/problem+json')) {
        const problem = await response.json() as Problem
        return { kind: 'http', status: response.status, problem }
    }

    if (response.status === 403) {
        let body: Partial<PackageForbidden> = {}
        try {
            body = await response.json() as PackageForbidden
        } catch {
            // [Authorize(Roles)] refusals have no body
        }
        dispatch(showForbidden({ package: body.package, access: body.access }))
    }

    return { kind: 'http', status: response.status }
}

export async function post(url: string, payload: object, headers: object = {}) {
    const response = await fetch(process.env.REACT_APP_REST_API + url, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
            'Content-Type': "application/json",
            ...headers,
        },
        redirect: "manual",

        referrerPolicy: "no-referrer",
        body: JSON.stringify(payload),
    });

    return response
}

export const postWithAuth = createAsyncThunk<any, { url: string, payload: object, }, AppThunkAPIType>(
    "http/postWithAuth", 
    async ({ url, payload }, { getState, dispatch, rejectWithValue }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "POST",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",

                referrerPolicy: "no-referrer",
                body: JSON.stringify(payload),
            })
            if (response.ok) {
                if (response.status === 204) {
                    return {}; 
                }
                return await response.json()
            } else {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
)


export const getWithAuth = createAsyncThunk<any, { url: string }, AppThunkAPIType>(
    "http/getWithAuth", 
    async ({ url }, { getState, dispatch, rejectWithValue }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "GET",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",

                referrerPolicy: "no-referrer",
            })
            if (response.ok) {
                return await response.json()
            } else {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
)


export const get = createAsyncThunk<any, { url: string, headers: object }, AppThunkAPIType>(
    "http/get",
    async ({ url, headers = {} }, { getState, dispatch, rejectWithValue }) => {
    try {
        const response = await fetch(process.env.REACT_APP_REST_API + url, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                'Content-Type': "application/json",
                ...headers,
            },
            redirect: "manual",

            referrerPolicy: "no-referrer",
        })
        if (response.ok) {
            return await response.json()
        } else {
            return rejectWithValue({
                kind: 'http',
                status: response.status,
            })
        }
    } catch (error) {
        return rejectWithValue({
            kind: "connection",
        })
    }
})

export async function put(url: string, payload: object, headers: object = {}) {
    const response = await fetch(process.env.REACT_APP_REST_API + url, {
        method: "PUT",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
            'Content-Type': "application/json",
            ...headers,
        },
        redirect: "manual",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(payload)
    })

    return response
}

export const putWithAuth = createAsyncThunk<any, { url: string, payload: object }, AppThunkAPIType>(
    "http/putWithAuth", 
    async ({ url, payload }, { rejectWithValue, dispatch, getState }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "PUT",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",
                referrerPolicy: "no-referrer",
                body: JSON.stringify(payload)
            })
            if (response.ok) {
                try {
                    return await response.json()
                } catch (error) {
                    // in case of no body
                    return {}
                }
            } else {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
)

export const patchWithAuth = createAsyncThunk<any, { url: string, payload: object }, AppThunkAPIType>(
    "http/patchWithAuth", 
    async ({ url, payload }, { rejectWithValue, dispatch, getState }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "PATCH",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",
                referrerPolicy: "no-referrer",
                body: JSON.stringify(payload)
            })
            if (response.ok) {
                try {
                    return await response.json()
                } catch (error) {
                    // in case of no body
                    return {}
                }
            } else {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
)

export async function del(url: string, headers: object = {}) {
    const response = await fetch(process.env.REACT_APP_REST_API + url, {
        method: "DELETE",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
            'Content-Type': "application/json",
            ...headers,
        },
        redirect: "manual",
        referrerPolicy: "no-referrer",
    })

    return response
}

export const deleteWithAuth = createAsyncThunk<any, { url: string }, AppThunkAPIType>(
    "http/deleteWithAuth", 
    async ({ url }, { getState, rejectWithValue, dispatch }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "DELETE",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",
                referrerPolicy: "no-referrer",
            })
            if (!response.ok) {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
) 

export const deleteWithAuthAndBody = createAsyncThunk<any, { url: string, payload?: object }, AppThunkAPIType>(
    "http/deleteWithAuth", 
    async ({ url, payload }, { getState, rejectWithValue, dispatch }) => {
        const authHeader = selectBearerToken(getState())
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "DELETE",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': "application/json",
                    ...authHeader,
                },
                redirect: "manual",
                referrerPolicy: "no-referrer",
                ...(payload ? { body: JSON.stringify(payload) } : {}),
            })
            if (response.ok) {
                if (response.status === 204) {
                    return {}
                }
                return await response.json()
            } else {
                return rejectWithValue(await httpError(response, dispatch))
            }
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            })
        }
    }
) 

export interface BlobResponse {
    blobUrl: string;
    fileName: string;
    fileType: string;
  }

export const getBlobWithAuth = createAsyncThunk<BlobResponse, { url: string }, AppThunkAPIType>(
    "http/getblobwithauth",
    async ({ url }, { getState, dispatch, rejectWithValue }) => {
        const authHeader = selectBearerToken(getState());
        try {
            const response = await fetch(process.env.REACT_APP_REST_API + url, {
                method: "GET",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/octet-stream',
                    ...authHeader,
                },
                redirect: "manual",
                referrerPolicy: "no-referrer",
            });

            if (!response.ok) {
                return rejectWithValue(await httpError(response, dispatch))
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const contentDisposition = response.headers.get('Content-Disposition');

            let fileName = 'unknown';
            const fileType = blob.type;
      
            if (contentDisposition) {
                // Szukamy fragmentu zawierającego "filename="
                const dispositionParts = contentDisposition.split(';');
                for (const part of dispositionParts) {
                  const trimmedPart = part.trim();
                  if (trimmedPart.startsWith('filename=')) {
                    fileName = trimmedPart.substring('filename='.length).replace(/^"|"$/g, '');
                    break;
                  }
                }
              }
           
            return { blobUrl, fileName, fileType };
        } catch (error) {
            return rejectWithValue({
                kind: "connection",
            });
        }
    }
);
